import React, {useCallback, useEffect, useMemo, useState} from "react";
import {CircularProgress, Grid2, Typography} from "@mui/material";
import {TimeControlDTO} from "@/api/data-contracts";
import {adminApi} from "@/boxhub-api";
import {useSnackbarEx} from "@/lib/snackbarex";
import {setGlobalDateDisplacement} from "@/lib/date-utils";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {DialogEx} from "@/lib/components/DialogEx";
import {useAuth} from "@/app/auth/AuthContext";
import {Constants} from "@/api/constants";
import DateInputField from "@/lib/components/inputfields/date-input-field";
import dayjs from "dayjs";
import {DateFormatter} from "@/lib/components/DateFormate";
import {useTranslation} from "react-i18next";
import {AdminI18N, CommonI18N} from "@/translations";

interface AdminDialogProps {
    open: boolean;
    onClose: () => void;
}

export const AdminDialog: React.FC<AdminDialogProps> = ({ open, onClose }: AdminDialogProps) => {
    const { environment, setTimeDisplacement } = useAuth();
    const dateFormat = useMemo(() => DateFormatter(), []);
    const [timeControl, setTimeControl] = useState<TimeControlDTO>();
    const { showMessage } = useSnackbarEx();
    const {t} = useTranslation();
    const [isDistanceCalculationRunning, setIsDistanceCalculationRunning] = useState(false);
    const [isGeocodingRunning, setIsGeocodingRunning] = useState(false);

    const loadTimeControl = useCallback(() => {
        if (environment !== Constants.ENV_PRODUCTION) {
            adminApi
                .getTimeControl()
                .then((res) => {
                    setTimeControl(res.data);
                    setGlobalDateDisplacement(res.data.displacement);
                    setTimeDisplacement(res.data.displacement);
                })
                    .catch((error) => showMessage({
                        summary: t(AdminI18N.control.message.loadTimeControlFailed),
                        severity: "error",
                        error
                    }));
        }
    }, [environment, setTimeDisplacement, showMessage, t]);

    useEffect(() => {
        loadTimeControl();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const timeControlPanel = timeControl && (
        <Grid2 container>
            <Grid2 size={{ sm: 12 }}>
                <Typography>{t(AdminI18N.control.page.timeControlTitle)}</Typography>
            </Grid2>
            <Grid2 size={{ sm: 4 }}>
                <DateInputField
                        label={t(AdminI18N.control.field.referenceDate)}
                    value={timeControl.today}
                    name="today"
                    error={
                        timeControl.displacement === 0
                                ? undefined
                                : {message: `${t(AdminI18N.control.field.systemDate)} ${dayjs(new Date()).format(dateFormat)}`}
                    }
                    onChange={(e) =>
                        setTimeControl((old) => ({
                            ...old,
                            today: e.value
                        }))
                    }
                />
            </Grid2>
            <Grid2>
                <ButtonEx
                        label={t(AdminI18N.control.action.setDate)}
                    variant="outlined"
                    color="error"
                    onClick={() => {
                        adminApi
                            .setTimeControl({
                                referenceDate: timeControl?.today
                                    ? dayjs(timeControl?.today).format("YYYY-MM-DD")
                                    : undefined
                            })
                            .then((res) => {
                                setTimeControl(res.data);
                                setGlobalDateDisplacement(res.data.displacement);
                                setTimeDisplacement(res.data.displacement);
                            })
                            .catch((error) =>
                                showMessage({
                                    summary: t(AdminI18N.control.message.setDateFailed),
                                    severity: "error",
                                    error
                                })
                            );
                    }}
                />
            </Grid2>
        </Grid2>
    );

    const buttons = (
            <ButtonEx label={t(CommonI18N.action.close)} data-cy="close-button" onClick={onClose} color="secondary"/>
    );

    return (
        <DialogEx
            title={t(AdminI18N.control.page.title)}
            data-cy={"admin-dialog"}
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            buttons={buttons}
        >
            <Grid2 container direction="column" padding={1} spacing={3} width="100%">
                {timeControlPanel}
                <Grid2 size={{ sm: 12 }}>
                    <Typography>{t(AdminI18N.control.page.jobControlTitle)}</Typography>
                </Grid2>
                <Grid2 container spacing={2} direction="row" width="100%">
                    <Grid2>
                        <ButtonEx
                                label={t(AdminI18N.control.action.requestWatch)}
                            color="warning"
                            onClick={() => {
                                adminApi
                                    .executeRequestWatch()
                                    .then(() =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.requestWatchSuccess),
                                            severity: "info"
                                        })
                                    )
                                    .catch((error) =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.requestWatchFailed),
                                            severity: "error",
                                            error
                                        })
                                    );
                            }}
                        />
                    </Grid2>
                    <Grid2>
                        <ButtonEx
                                label={t(AdminI18N.control.action.mailSend)}
                            color="warning"
                            onClick={() => {
                                adminApi
                                    .executeMailSender()
                                    .then(() =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.mailSendSuccess),
                                            severity: "info"
                                        })
                                    )
                                    .catch((error) =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.mailSendFailed),
                                            severity: "error",
                                            error
                                        })
                                    );
                            }}
                        />
                    </Grid2>
                    <Grid2>
                        <ButtonEx
                                label={t(AdminI18N.control.action.companyWatch)}
                            color="warning"
                            onClick={() => {
                                adminApi
                                    .executeCompanyWatch()
                                    .then(() =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.companyWatchSuccess),
                                            severity: "info"
                                        })
                                    )
                                    .catch((error) =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.companyWatchFailed),
                                            severity: "error",
                                            error
                                        })
                                    );
                            }}
                        />
                    </Grid2>
                    <Grid2>
                        <ButtonEx
                                label={t(AdminI18N.control.action.documentRetention)}
                            color="warning"
                            onClick={() => {
                                adminApi
                                    .executeDocumentRetention()
                                    .then(() =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.documentRetentionSuccess),
                                            severity: "info"
                                        })
                                    )
                                    .catch((error) =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.documentRetentionFailed),
                                            severity: "error",
                                            error
                                        })
                                    );
                            }}
                        />
                    </Grid2>
                    <Grid2>
                        <ButtonEx
                                label={t(AdminI18N.control.action.orderWatch)}
                            color="warning"
                            onClick={() => {
                                adminApi
                                    .executeOrderWatch()
                                    .then(() =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.orderWatchSuccess),
                                            severity: "info"
                                        })
                                    )
                                    .catch((error) =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.orderWatchFailed),
                                            severity: "error",
                                            error
                                        })
                                    );
                            }}
                        />
                    </Grid2>
                    <Grid2>
                        <ButtonEx
                            label={
                                isDistanceCalculationRunning ? (
                                    <CircularProgress size={24} />
                                ) : (
                                        t(AdminI18N.control.action.distanceCalculation)
                                )
                            }
                            color="warning"
                            disabled={isDistanceCalculationRunning}
                            onClick={() => {
                                setIsDistanceCalculationRunning(true);
                                adminApi
                                    .executeDistanceCalculation()
                                    .then(() =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.distanceCalculationSuccess),
                                            severity: "info"
                                        })
                                    )
                                    .catch((error) =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.distanceCalculationFailed),
                                            severity: "error",
                                            error
                                        })
                                    )
                                    .finally(() => setIsDistanceCalculationRunning(false));
                            }}
                        />
                    </Grid2>
                    <Grid2>
                        <ButtonEx
                                label={isGeocodingRunning ?
                                        <CircularProgress size={24}/> : t(AdminI18N.control.action.geocoding)}
                            color="warning"
                            disabled={isGeocodingRunning}
                            onClick={() => {
                                setIsGeocodingRunning(true);
                                adminApi
                                    .executeGeocoding()
                                    .then(() =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.geocodingSuccess),
                                            severity: "info"
                                        })
                                    )
                                    .catch((error) =>
                                        showMessage({
                                            summary: t(AdminI18N.control.message.geocodingFailed),
                                            severity: "error",
                                            error
                                        })
                                    )
                                    .finally(() => setIsGeocodingRunning(false));
                            }}
                        />
                    </Grid2>
                </Grid2>
            </Grid2>
        </DialogEx>
    );
};
